import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import axiosInstance from '../../axiosConfig';
import {
  MarketplaceFooter,
  MarketplaceHeader,
  MarketplaceProductImage,
  MarketplaceSectionHeading,
  MarketplaceStoreChip,
  MarketplaceStoreTile,
  ProductCard,
  ProductRailCard,
  getMarketplaceProductPath,
  getMarketplacePath,
  marketplaceCurrency,
  trackSponsoredClick,
  trackSponsoredImpression,
  useMarketplaceProductGroups
} from './MarketplaceShared';
import MarketplaceSeo, { marketplaceUrlFor } from './MarketplaceSeo';
import './Marketplace.css';

const CATALOG_PAGE_SIZE = 12;

function MarketplaceHomePage() {
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [catalogPage, setCatalogPage] = useState(1);
  const [catalogItems, setCatalogItems] = useState([]);
  const [catalogPageCount, setCatalogPageCount] = useState(1);
  const [catalogLoading, setCatalogLoading] = useState(false);

  useEffect(() => {
    let alive = true;

    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const { data } = await axiosInstance.get('/marketplace/home');
        if (!data?.success) {
          throw new Error(data?.message || 'Failed to load marketplace');
        }
        if (alive) setPayload(data);
      } catch (err) {
        if (alive) setError(err?.response?.data?.message || err.message || 'Failed to load marketplace');
      } finally {
        if (alive) setLoading(false);
      }
    };

    load();
    return () => {
      alive = false;
    };
  }, []);

  const featuredMerchants = payload?.merchants?.featured || [];
  const latestMerchants = payload?.merchants?.latest || [];
  const sliderMerchants = payload?.merchants?.slider || [];
  const products = payload?.products || [];
  const categoryRows = useMemo(() => payload?.categoryRows || [], [payload]);
  const picksFallback = useMemo(() => payload?.picksFallback || [], [payload]);
  const boosts = payload?.boosts || {};
  const hero = payload?.hero || {};
  const sections = payload?.config?.sections || {};
  const groups = useMarketplaceProductGroups(products);

  const categoryHighlights = useMemo(() => {
    const map = new Map();
    products.forEach((product) => {
      const key = String(product.category || 'General').trim() || 'General';
      if (!map.has(key)) {
        map.set(key, { name: key, count: 0, image: product.image || null });
      }
      const current = map.get(key);
      current.count += 1;
      if (!current.image && product.image) current.image = product.image;
    });
    // Only surface categories with a meaningful catalogue behind them — anything
    // under 5 products reads as noise on the belt ("General · 1 products"
    // isn't a discovery signal, it's clutter).
    return [...map.values()]
      .filter((row) => row.count >= 5)
      .sort((a, b) => b.count - a.count)
      .slice(0, 6);
  }, [products]);
  const categoryRailItems = useMemo(() => {
    if (!categoryHighlights.length) return [];
    return [...categoryHighlights, ...categoryHighlights];
  }, [categoryHighlights]);

  const leadMerchants = featuredMerchants.length ? featuredMerchants : latestMerchants;
  const merchantStrip = sliderMerchants.length ? sliderMerchants : leadMerchants;
  const heroProducts = useMemo(() => {
    const seen = new Set();
    return [...groups.sponsoredProducts, ...groups.featuredGrid]
      .filter((product) => {
        if (!product?.id || seen.has(product.id)) return false;
        seen.add(product.id);
        return true;
      })
      .slice(0, 6);
  }, [groups.featuredGrid, groups.sponsoredProducts]);
  const [heroIndex, setHeroIndex] = useState(0);
  const heroProduct = heroProducts[heroIndex % Math.max(heroProducts.length, 1)] || groups.heroProduct;
  const trendingProducts = groups.featuredGrid.slice(0, 10);
  const newProducts = groups.railProducts.slice(0, 32);
  const heroSideProducts = groups.railProducts.slice(1, 5);

  // Source of the "Marketplace picks" section:
  //   1. Sponsored placements when boosts are running (uses trendingProducts
  //      which is sponsored-first from featuredGrid) — preserves the old
  //      priority-of-boosts behaviour.
  //   2. Admin-curated picksFallback when no boosts and admin has configured
  //      it — comes from a separate backend pool that DOES NOT feed the
  //      hero, side products, or the main catalog.
  //   3. Auto-selected trendingProducts otherwise — old fallback.
  const picksSectionProducts = useMemo(() => {
    if (boosts.sponsoredVisible) return trendingProducts;
    if (picksFallback.length) return picksFallback;
    return trendingProducts;
  }, [boosts.sponsoredVisible, picksFallback, trendingProducts]);

  // Dedupe products across sections so the same item never appears twice on the
  // homepage. Sponsored placement priority is preserved because the hero and
  // Marketplace picks sections are resolved first — subsequent sections just
  // skip anything already surfaced above.
  const dedupedSections = useMemo(() => {
    const seen = new Set();
    if (heroProduct?.id) seen.add(heroProduct.id);
    heroSideProducts.forEach((p) => {
      if (p?.id) seen.add(p.id);
    });

    const trending = picksSectionProducts.filter((p) => p?.id && !seen.has(p.id));
    trending.forEach((p) => seen.add(p.id));

    const rows = (categoryRows || []).map((row) => {
      const filtered = Array.isArray(row.products)
        ? row.products.filter((p) => p?.id && !seen.has(p.id))
        : [];
      filtered.forEach((p) => seen.add(p.id));
      return { ...row, products: filtered };
    });

    return { trending, rows, sectionIds: Array.from(seen) };
  }, [heroProduct, heroSideProducts, picksSectionProducts, categoryRows]);

  // Stable key so the fetch effect only re-runs when the set of items
  // shown above the catalog actually changes — not on every render.
  const catalogExcludeKey = useMemo(
    () => dedupedSections.sectionIds.slice(0, 100).sort().join(','),
    [dedupedSections.sectionIds]
  );

  // Fetch the paginated catalog from the marketplace-wide endpoint so
  // shoppers can walk every eligible product via Prev/Next, not just
  // the 24 items the homepage response ships. Exclude the ids already
  // rendered above so pages don't repeat what's on screen.
  useEffect(() => {
    let alive = true;
    setCatalogLoading(true);
    axiosInstance
      .get('/marketplace/products', {
        params: {
          page: catalogPage,
          limit: CATALOG_PAGE_SIZE,
          exclude: catalogExcludeKey || undefined
        }
      })
      .then(({ data }) => {
        if (!alive) return;
        setCatalogItems(Array.isArray(data?.products) ? data.products : []);
        setCatalogPageCount(Math.max(1, Number(data?.pagination?.pages) || 1));
      })
      .catch(() => {
        if (alive) {
          setCatalogItems([]);
          setCatalogPageCount(1);
        }
      })
      .finally(() => {
        if (alive) setCatalogLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [catalogPage, catalogExcludeKey]);

  // Reset to page 1 if the visible-above-set changes (e.g. hero
  // rotated), or if server reports fewer pages than the current page.
  useEffect(() => {
    setCatalogPage(1);
  }, [catalogExcludeKey]);

  useEffect(() => {
    if (catalogPage > catalogPageCount) setCatalogPage(catalogPageCount);
  }, [catalogPage, catalogPageCount]);

  useEffect(() => {
    setHeroIndex(0);
    setCatalogPage(1);
  }, [heroProducts.length]);

  useEffect(() => {
    setCatalogPage(1);
  }, [products.length]);

  useEffect(() => {
    if (heroProducts.length < 2) return undefined;
    const timer = window.setInterval(() => {
      setHeroIndex((current) => (current + 1) % heroProducts.length);
    }, 6500);
    return () => window.clearInterval(timer);
  }, [heroProducts.length]);

  useEffect(() => {
    const heroCampaignId = heroProduct?.sponsorship?.campaignId;
    if (heroCampaignId) trackSponsoredImpression(heroCampaignId, `hero-${heroProduct.id}`);
    heroSideProducts.forEach((product) => {
      if (product?.sponsorship?.campaignId) {
        trackSponsoredImpression(product.sponsorship.campaignId, `hero-side-${product.id}`);
      }
    });
  }, [heroProduct, heroSideProducts]);

  if (loading) {
    return (
      <div className="marketplace-page">
        <MarketplaceHeader active="home" />
        <div className="marketplace-shell marketplace-shell--centered">
          <section className="marketplace-feedback-card marketplace-loading-card">
            <h3>Loading marketplace</h3>
            <p>Pulling live stores, boosted products, and storefront previews.</p>
          </section>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="marketplace-page">
        <MarketplaceHeader active="home" />
        <div className="marketplace-shell marketplace-shell--centered">
          <section className="marketplace-feedback-card">
            <h3>Marketplace unavailable</h3>
            <p>{error}</p>
          </section>
        </div>
      </div>
    );
  }

  const heroTitle = hero?.title || 'Discover standout stores and products from Revstar merchants';
  const heroDescription = hero?.description
    || 'Browse featured merchants, inspect their best products, and jump straight into their live storefronts.';
  const homeJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'Revstar Marketplace',
    url: marketplaceUrlFor('/'),
    potentialAction: {
      '@type': 'SearchAction',
      target: `${marketplaceUrlFor('/merchants')}?search={search_term_string}`,
      'query-input': 'required name=search_term_string'
    }
  };

  return (
    <div className="marketplace-page">
      <MarketplaceSeo
        title={heroTitle}
        description={heroDescription}
        path="/"
        jsonLd={homeJsonLd}
      />
      <MarketplaceHeader active="home" />

      <div className="marketplace-shell marketplace-shell--spacious">
        <section className="marketplace-hero">
          {heroProduct ? (
            <div className="marketplace-hero-feature">
              <Link
                className="marketplace-hero-campaign"
                to={getMarketplaceProductPath(heroProduct, heroProduct.sponsorship?.campaignId)}
                onClick={() => trackSponsoredClick(heroProduct.sponsorship?.campaignId)}
              >
                <div className="marketplace-hero-campaign-copy">
                  <span className={`marketplace-hero-product-kicker${heroProduct.sponsorship ? ' marketplace-hero-product-kicker--sponsored' : ''}`}>
                    {heroProduct.sponsorship ? 'Sponsored' : 'Featured product'}
                  </span>
                  <h1>{heroProduct.name}</h1>
                  <div className="marketplace-hero-campaign-price">
                    <strong>{marketplaceCurrency(heroProduct.price, heroProduct.currency)}</strong>
                    {heroProduct.store?.name ? (
                      <span>Sold by <b>{heroProduct.store.name}</b></span>
                    ) : null}
                  </div>
                  <span className="marketplace-hero-campaign-cta">
                    View on merchant store
                    <ArrowRight size={17} />
                  </span>
                </div>
                <div className="marketplace-hero-campaign-media">
                  <MarketplaceProductImage src={heroProduct.image} alt={heroProduct.name} variant="hero" minCoverWidth={600} />
                </div>
              </Link>
              {heroProducts.length > 1 ? (
                <div className="marketplace-hero-slider-controls" aria-label="Featured product carousel">
                  {heroProducts.map((product, index) => (
                    <button
                      key={product.id}
                      type="button"
                      className={index === heroIndex ? 'is-active' : ''}
                      aria-label={`Show ${product.name}`}
                      onClick={() => setHeroIndex(index)}
                    />
                  ))}
                </div>
              ) : null}
              </div>
          ) : (
            <section className="marketplace-empty-hero">
              <span className="marketplace-section-eyebrow">{hero.eyebrow || 'Revstar marketplace'}</span>
              <h1>{hero.title || 'Discover products from live Revstar storefronts.'}</h1>
              <p>{hero.description || 'Browse products, compare stores, and step directly into the merchant’s live shop to complete your purchase.'}</p>
            </section>
          )}

          {heroSideProducts.length ? (
            <div className="marketplace-hero-side-grid">
              {heroSideProducts.map((product) => (
                <Link
                  key={product.id}
                  className="marketplace-hero-mini-card"
                  to={getMarketplaceProductPath(product, product.sponsorship?.campaignId)}
                  onClick={() => trackSponsoredClick(product.sponsorship?.campaignId)}
                >
                  <div className="marketplace-hero-mini-card-media">
                    <MarketplaceProductImage src={product.image} alt={product.name} variant="mini" />
                  </div>
                  <div className="marketplace-hero-mini-card-copy">
                    <strong>{product.name}</strong>
                    {product.store?.name ? <span>Sold by {product.store.name}</span> : null}
                    <b>{marketplaceCurrency(product.price, product.currency)}</b>
                  </div>
                </Link>
              ))}
            </div>
          ) : null}
        </section>

        {categoryHighlights.length ? (
          <section className="marketplace-category-belt" aria-label="Featured categories">
            <div className="marketplace-category-belt-track">
              {categoryRailItems.map((category, index) => {
                const duplicate = index >= categoryHighlights.length;
                return (
                  <Link
                    key={`${category.name}-${index}`}
                    className="marketplace-category-belt-item"
                    to={`${getMarketplacePath('/merchants')}?search=${encodeURIComponent(category.name)}`}
                    aria-hidden={duplicate ? 'true' : undefined}
                    tabIndex={duplicate ? -1 : undefined}
                  >
                    <div className="marketplace-category-belt-thumb">
                      {category.image ? <img src={category.image} alt={duplicate ? '' : category.name} /> : <div className="marketplace-image-fallback" />}
                    </div>
                    <div>
                      <strong>{category.name}</strong>
                      <span>{category.count} {category.count === 1 ? 'product' : 'products'}</span>
                    </div>
                  </Link>
                );
              })}
            </div>
          </section>
        ) : null}

        {dedupedSections.trending.length ? (
          <section className="marketplace-section">
            <MarketplaceSectionHeading
              eyebrow={boosts.sponsoredVisible ? 'Sponsored placements' : 'Marketplace picks'}
              title={boosts.sponsoredVisible ? 'Sponsored picks' : 'Shop the marketplace'}
              description={boosts.directoryOnlyMode
                ? 'The marketplace is currently prioritising merchant discovery.'
                : ''}
            />
            <div className="marketplace-product-grid marketplace-product-grid--dense">
              {dedupedSections.trending.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          </section>
        ) : null}

        {sections.featuredMerchants !== false && leadMerchants.length >= 3 ? (
          <section className="marketplace-section">
            <MarketplaceSectionHeading
              eyebrow="Shop by brand"
              title="Featured stores"
              description=""
              actionLabel="View all stores"
              actionTo={getMarketplacePath('/merchants')}
            />
            <div className="marketplace-merchant-grid">
              {leadMerchants.slice(0, 8).map((merchant) => (
                <MarketplaceStoreTile key={merchant.slug} merchant={merchant} />
              ))}
            </div>
          </section>
        ) : null}

        {dedupedSections.rows.filter((row) => row.products?.length).map((row) => (
          <section key={row.slug || row.category} className="marketplace-section">
            <MarketplaceSectionHeading
              eyebrow="Shop by category"
              title={row.title}
            />
            <div className="marketplace-category-row" role="list">
              {row.products.map((product) => (
                <div key={product.id} className="marketplace-category-row-item" role="listitem">
                  <ProductCard product={product} />
                </div>
              ))}
            </div>
          </section>
        ))}

        {(catalogItems.length > 0 || catalogPageCount > 1) ? (
          <section className="marketplace-section">
            <MarketplaceSectionHeading
              eyebrow="More to browse"
              title="Fresh from Revstar stores"
              description=""
            />
            <div
              className="marketplace-product-grid marketplace-product-grid--catalog"
              aria-busy={catalogLoading || undefined}
            >
              {catalogItems.map((product) => (
                <ProductRailCard key={product.id} product={product} />
              ))}
            </div>
            {catalogPageCount > 1 ? (
              <div className="marketplace-home-pagination" aria-label="Marketplace product pages">
                <button
                  type="button"
                  disabled={catalogPage === 1 || catalogLoading}
                  onClick={() => setCatalogPage((page) => Math.max(1, page - 1))}
                >
                  Previous
                </button>
                <span>Page {catalogPage} of {catalogPageCount}</span>
                <button
                  type="button"
                  disabled={catalogPage === catalogPageCount || catalogLoading}
                  onClick={() => setCatalogPage((page) => Math.min(catalogPageCount, page + 1))}
                >
                  Next
                </button>
              </div>
            ) : null}
          </section>
        ) : null}

        {sections.featuredMerchants !== false && leadMerchants.length ? (
          <section className="marketplace-section">
            <MarketplaceSectionHeading
              eyebrow="Popular stores"
              title="Stores on Revstar"
              description=""
              actionLabel="View all stores"
              actionTo={getMarketplacePath('/merchants')}
            />
            <div className="marketplace-store-strip marketplace-store-strip--home">
              {merchantStrip.slice(0, 8).map((merchant) => (
                <MarketplaceStoreChip key={merchant.slug} merchant={merchant} />
              ))}
            </div>
          </section>
        ) : null}

        <section className="marketplace-section marketplace-merchant-cta" aria-label="Become a Revstar merchant">
          <div className="marketplace-merchant-cta-copy">
            <span className="marketplace-merchant-cta-eyebrow">Sell on Revstar</span>
            <h2>Turn your business into a live storefront in minutes.</h2>
            <p>Get your own subdomain, take card and bank-transfer payments, run POS and WhatsApp orders — and get discovered on the Revstar Marketplace alongside merchants already selling.</p>
          </div>
          <a
            className="marketplace-merchant-cta-btn"
            href="https://app.userevstar.com/signup"
            target="_blank"
            rel="noreferrer"
          >
            Start selling — it's free
            <ArrowRight size={16} />
          </a>
        </section>
      </div>
      <MarketplaceFooter />
    </div>
  );
}

export default MarketplaceHomePage;
